/**
 * @module app.interaction.ModifyTrack
 */
import appInteractionTrack from '../interaction/Track.js';
import olCollection from 'ol/Collection.js';
import olFeature from 'ol/Feature.js';
import olGeomLineString from 'ol/geom/LineString.js';
import olGeomPoint from 'ol/geom/Point.js';
import olInteractionSnap from 'ol/interaction/Snap.js';
import olLayerVector from 'ol/layer/Vector.js';
import olSourceVector from 'ol/source/Vector.js';
import {create2DLineStringFrom2Points} from './interactionutil.js';
import {
  Fill as olStyleFill,
  RegularShape as olStyleRegularShape, Stroke as olStyleStroke, Style as olStyleStyle} from 'ol/style';
import {asArray as colorAsArray} from "ol/color";
import {remove as olArrayRemove} from 'ol/array';
import {TRACK_WIDTH, Z_INDEX} from "../constants";


/**
 * @private
 * @param {string} trackColor
 * @param {boolean} small
 * @return {Array.<ol.style.Style>}
 */
const createStyle_ = function(trackColor, small) { // TODO check this
  const trackColorRGBA = colorAsArray(trackColor);
  const color = trackColorRGBA.slice(0, 3).map(channel => 0.6 * channel);
  return [new olStyleStyle({
    fill: new olStyleFill({
      color,
    }),
    stroke: new olStyleStroke({
      color,
      width: TRACK_WIDTH,
    }),
    image: new olStyleRegularShape({
      fill: new olStyleFill({
        color,
      }),
      stroke: new olStyleStroke({
        color,
        width: 1,
      }),
      points: 4,
      radius: small ? 8 : 10,
      angle: Math.PI / 4
    })
  })];
};


/**
 * Interaction to modify a track.
 *
 */
class ModifyTrack extends appInteractionTrack {
  constructor(options) {
    super(options);

    const opt = options;
    opt.handleEvent = this.handleEvent;
    opt.handleDownEvent = this.handleDownEvent;
    opt.handleDragEvent = this.handleDragEvent;
    opt.handleMoveEvent = this.handleMoveEvent;
    opt.handleUpEvent = this.handleUpEvent;

    const defaultModifyStyle = (() => {
      const cache = {};

      const fn = (feature, resolution) => {
        const small = feature.get('small') || false;
        const color = this.sketchLineColor.color_rgb || options.color;
        let cached = cache[`${small}-${color}`];
        if (!cached) {
          cached = createStyle_(color, small);
          cache[`${small}-${color}`] = Object.freeze(cached);
        }
        return cached;
      };
      return fn;
    })();

    this.snapFeatures_ = new olCollection();
    this.snapInteraction_ = new olInteractionSnap({
      features: this.snapFeatures_
    });

    /**
     * The coordinate of the last registered event when dragging.
     * @type {ol.Pixel}
     * @private
     */
    this.coordinate_ = null;

    /**
     * The feature that is being dragged.
     * @type {ol.Feature}
     * @private
     */
    this.dragFeature_ = null;

    /**
     * If the feature was dragged after a `down` event. Needed to
     * register clicks without dragging.
     * @type {boolean}
     * @private
     */
    this.wasDragged_ = false;

    /**
     * Sketch point.
     * @type {ol.Feature}
     * @private
     */
    this.sketchPoint_ = null;

    /**
     * If inserting a new point or dragging an existing control point.
     * @type {boolean}
     * @private
     */
    this.insertingNewPoint_ = false;

    /**
     * The last point that was dragged.
     * @type {ol.Feature}
     * @private
     */
    this.lastPoint_ = null;

    /**
     * @type {ol.source.Vector}
     * @private
     */
    this.sketchSource_ = new olSourceVector();

    /**
     * @type {ol.layer.Vector}
     * @private
     */
    this.sketchLayer_ = new olLayerVector({
      'name': 'modify track sketch',
      source: this.sketchSource_,
      style: defaultModifyStyle,
      zIndex: Z_INDEX.UP
    });

    /**
     * If the distance between the original point and the mapped point
     * is less than this tolerance, the point is considered on the network.
     * @type {number}
     */
    this.onNetworkTolerance = 5;

    /**
     * Should the points and segments be snapped?
     * @type {boolean}
     */
    this.snap = options.snap;

    this.sketchLineColor = {
      color_rgb: undefined,
      opacity: 1,
    };
  }

  /**
   * @inheritDoc
   */
  setMap(map) {
    super.setMapWithLayers(map);

    // FIXME: use this.sketchLayer_.setMap(map)

    const prevMap = this.snapInteraction_.getMap();
    if (prevMap) {
      prevMap.removeInteraction(this.snapInteraction_);
      prevMap.removeLayer(this.sketchLayer_);
    }

    if (map) {
      map.addInteraction(this.snapInteraction_);
      map.addLayer(this.sketchLayer_);
    }
  }

  /**
   * @param {ol.Feature} track The track feature.
   * @param {boolean} skipSourcesUpdate - not change control points on UI if true
   */
  setTrack(track, skipSourcesUpdate = false) {
    const trackG = /** @type {ol.geom.LineString} */ (track.getGeometry());
    let vpoints = /** @type {Array.<ol.Coordinate>} */ (track.get('via_points'));

    const trackpoints = trackG.getCoordinates();
    if (!vpoints) {
      vpoints = [trackG.getFirstCoordinate(), trackG.getLastCoordinate()];
    }

    /** @type {Array.<ol.Feature>} */
    const controlPoints = this.controlPoints = vpoints.map(function(xy) {
      return new olFeature(new olGeomPoint(xy));
    });

    /**
     * @param {ol.geom.Point} toPoint
     * @param {number} startIndex
     * @return {number} index of searched poin
     */
    const trackIndexOf = function(toPoint, startIndex) {
      console.assert(startIndex <= trackpoints.length);
      const c1 = toPoint.getCoordinates();
      for (let i = startIndex; i < trackpoints.length; ++i) {
        const c2 = trackpoints[i];
        if (Math.abs(c1[0] - c2[0]) + Math.abs(c1[1] - c2[1]) < 0.1) {
          return i;
        }
      }
      return -1;
    };

    /** @type {Array.<ol.Feature>} */
    const segments = this.segments = [];
    let i, startIndex, point, previousPoint, segment;
    for (i = 1, startIndex = 0; i < controlPoints.length; i++) {
      const pointG = /** @type {ol.geom.Point} */ (controlPoints[i].getGeometry());
      const endIndex = trackIndexOf(pointG, startIndex);
      if (endIndex === -1) {
        // We want this error message
        console.log('Control point', i, pointG.getCoordinates(), 'not in track');
        break;
      }

      const coordinates = trackpoints.slice(startIndex, endIndex + 1);
      segments.push(new olFeature(new olGeomLineString(coordinates)));
      startIndex = endIndex;
    }

    if (segments.length + 1 !== controlPoints.length) {
      // We want this error message
      console.log('Error, resetting the control points of', trackpoints);
      segments.length = 0;
      segments.push(track);
      controlPoints.length = 0;
      controlPoints.push(new olFeature(new olGeomPoint(trackpoints[0])));
      controlPoints.push(new olFeature(new olGeomPoint(trackpoints[trackpoints.length - 1])));
    }

    this.snapFeatures_.clear();
    for (i = 0; i < controlPoints.length; i++) {
      point = controlPoints[i];

      this.snapFeatures_.push(point);
      if (i === 0) {
        point.set('segmentIn', null);
        point.set('segmentOut', 0 < segments.length ? segments[0] : null);
      } else {
        segment = segments[i - 1];
        previousPoint = controlPoints[i - 1];
        point.set('segmentIn', segment);
        point.set('segmentOut', i < segments.length ? segments[i] : null);
        this.snapFeatures_.push(segment);

        segment.set('from', previousPoint);
        segment.set('to', point);
      }
    }

    if (!skipSourcesUpdate) {
      this.pointSource.clear();
      this.pointSource.addFeatures(controlPoints);
      this.segmentSource.clear();
      this.segmentSource.addFeatures(segments);
    }
    if (!this.sketchLineColor.color_rgb) {
      this.updateSketchLineColor(track.get('color_rgb'), track.get('opacity') / 100);
    }
  }

  /**
   * @param {ol.MapBrowserEvent} evt Map browser event.
   * @return {boolean} `true` to start the drag sequence.
   */
  handleDownEvent(evt) {
    if (evt.map.getView().getInteracting()) {
      // Ignore events from pinch zoom
      return false;
    }
    this.wasDragged_ = false;
    const map = evt.map;

    let feature = map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
      // skip the sketch-point and not valid line
      if (feature === this.sketchPoint_ || !this.isValidTrackFeature(feature)) {
        return undefined;
      } else {
        return feature;
      }
    });

    if (feature) {
      this.insertingNewPoint_ = feature.getGeometry().getType() === 'LineString';
      if (this.insertingNewPoint_) {
        const coo = evt.coordinate; // this is 2D
        const newPoint = new olFeature(new olGeomPoint(coo));
        this.insertControlPoint_(feature, newPoint);
        feature = newPoint;
      }

      this.coordinate_ = evt.coordinate;
      this.dragFeature_ = feature;
      this.clearSketchPoint_();
      this.snapInteraction_.setActive(false);
    }

    return !!feature;
  }

  /**
   * @param {ol.Feature} segment The segment that will be split by the new point.
   * @param {ol.Feature} controlPoint The new control point.
   * @private
   */
  insertControlPoint_(segment, controlPoint) {
    const pointFrom = /** @type {ol.Feature} */ (segment.get('from'));
    const pointTo = /** @type {ol.Feature} */ (segment.get('to'));

    const segment1 = this.createSegment_(pointFrom, controlPoint);
    const segment2 = this.createSegment_(controlPoint, pointTo);

    this.replaceSegment_(segment, segment1, segment2);

    const insertIndex = this.controlPoints.indexOf(pointTo);
    this.controlPoints.splice(insertIndex, 0, controlPoint);
    this.pointSource.addFeature(controlPoint);
    this.snapFeatures_.push(controlPoint);
  }

  /**
   * @param {ol.Feature} featureFrom Start point.
   * @param {ol.Feature} featureTo End point.
   * @return {ol.Feature} Segment between the two points.
   * @private
   */
  createSegment_(featureFrom, featureTo) {
    const pointFrom = /** @type {ol.geom.Point} */ (featureFrom.getGeometry());
    const pointTo = /** @type {ol.geom.Point} */ (featureTo.getGeometry());
    const segment = new olFeature({
      geometry: create2DLineStringFrom2Points(pointFrom, pointTo)
    });

    featureFrom.set('segmentOut', segment);
    featureTo.set('segmentIn', segment);

    segment.set('from', featureFrom);
    segment.set('to', featureTo);

    return segment;
  }

  /**
   * Replace a segment with two new segments.
   * @param {ol.Feature} oldSegment Old segment.
   * @param {ol.Feature} newSegment1 New segment 1.
   * @param {ol.Feature} newSegment2 New segment 2.
   * @private
   */
  replaceSegment_(oldSegment, newSegment1, newSegment2) {
    const oldIndex = this.segments.indexOf(oldSegment);
    this.segments.splice(oldIndex, 1, newSegment1, newSegment2);

    // update overlay
    this.segmentSource.removeFeature(oldSegment);
    this.segmentSource.addFeature(newSegment1);
    this.segmentSource.addFeature(newSegment2);

    // update snap index
    this.snapFeatures_.remove(oldSegment);
    this.snapFeatures_.push(newSegment1);
    this.snapFeatures_.push(newSegment2);
  }

  /**
   * Updates the connected segments after a control point was dragged.
   * @param {ol.Feature} controlPoint Point.
   * @private
   */
  updateControlPoint_(controlPoint) {
    const point = /** @type {ol.geom.Point} */ (controlPoint.getGeometry());
    let segment;
    if (controlPoint && controlPoint.get('segmentIn')) {
      segment = controlPoint.get('segmentIn');
      segment.setGeometry(create2DLineStringFrom2Points(segment.get('from').getGeometry(), point));
    }
    if (controlPoint && controlPoint.get('segmentOut')) {
      segment = controlPoint.get('segmentOut');
      segment.setGeometry(create2DLineStringFrom2Points(point, segment.get('to').getGeometry()));
    }
  }

  /**
   * @param {ol.MapBrowserEvent} evt Map browser event.
   */
  handleDragEvent(evt) {
    if (evt.map.getView().getInteracting()) {
      // Ignore events from pinch zoom
      return false;
    }
    this.wasDragged_ = true;
    const geometry = /** @type {ol.geom.Point} */ (this.dragFeature_.getGeometry());
    geometry.setCoordinates([evt.coordinate[0], evt.coordinate[1]]);
    this.updateControlPoint_(this.dragFeature_);
    this.coordinate_[0] = evt.coordinate[0];
    this.coordinate_[1] = evt.coordinate[1];
  }

  /**
   * @param {ol.MapBrowserEvent} evt Event.
   */
  handleMoveEvent(evt) {
    if (evt.dragging) {
      return;
    }

    const map = evt.map;
    const feature = map.forEachFeatureAtPixel(evt.pixel, (feature, layer) => {
      if (feature === this.sketchPoint_ || !this.isValidTrackFeature(feature)) {
        return undefined;
      } else {
        return feature;
      }
    });

    if (feature) {
      // show a larger symbol for real control-points, and a smaller one
      // for snap-points on lines
      const smallPoint = !(feature.get('segmentIn') && feature.get('segmentOut'));
      this.createOrUpdateSketchPoint_(evt, smallPoint);
    } else {
      this.clearSketchPoint_();
    }
  }

  /**
   * @private
   */
  clearSketchPoint_() {
    if (this.sketchPoint_) {
      this.sketchSource_.removeFeature(this.sketchPoint_);
      this.sketchPoint_ = null;
    }
  }

  /**
   * @param {ol.MapBrowserEvent} evt Map browser event.
   * @return {boolean} `false` to stop the drag sequence.
   */
  async handleUpEvent(evt) {
    if (evt.map.getView().getInteracting()) {
      // Ignore events from pinch zoom
      return false;
    }
    this.lastPoint_ = this.dragFeature_;
    if (!this.wasDragged_ && !this.insertingNewPoint_) {
      // click on control-point, remove the point and request a route
      const newSegment = this.removeControlPoint_(this.lastPoint_);
      if (newSegment) {
        await this.routeSegmentIfPossible_(newSegment);
        this.dispatchSegmentChangeEvent();
      }
    } else if (this.snap) {
      const snappedPoint = await this.requestSnapPoint(this.dragFeature_);
      if (snappedPoint) {
        this.snapPoint_(this.dragFeature_, snappedPoint);
      }
      // if the request was not successful simply keep the original point
    } else {
      this.dispatchSegmentChangeEvent();
    }
    this.coordinate_ = null;
    this.dragFeature_ = null;

    this.snapInteraction_.setActive(true);

    return false;
  }

  /**
   * Route a segment if both ends are on the routing network.
   * @param {ol.Feature} segment
   * @private
   */
  async routeSegmentIfPossible_(segment) {
    const pointFrom = /** @type {ol.Feature} */ (segment.get('from'));
    const geomFrom = /** @type {ol.geom.Point} */ (pointFrom.getGeometry());
    const pointTo = /** @type {ol.Feature} */ (segment.get('to'));
    const geomTo = /** @type {ol.geom.Point} */ (pointTo.getGeometry());

    /**
     * @param {ol.Coordinate} c1
     * @param {ol.Coordinate} c2
     * @return {boolean}
     */
    const coordinateEquals = function(c1, c2) {
      const length = c1.length;
      if (c1.length !== c2.length) {
        return false;
      }
      for (let i = 0; i < length; ++i) {
        if (Math.abs(c1[i] - c2[i]) > 1) { // 1 meter
          return false;
        }
      }
      return true;
    };

    // Checking if both points are on the routing network
    // TODO: if we stored the information about being or not on the network
    // then this initial checking may be removed.
    // One (drawback?) would be that if the network change, a point on the
    // network may not stay on it. However it is very unlikely and not a big
    // issue for the user: in this case there will be a visible snapping on the
    // map.
    const mappedPointFrom = await this.requestSnapPoint(pointFrom);
    const mappedCoordFrom = mappedPointFrom.getCoordinates();
    const coordFrom = geomFrom.getCoordinates();
    if (coordinateEquals(mappedCoordFrom, coordFrom)) {
      const mappedPointTo = await this.requestSnapPoint(pointTo);
      const mappedCoordTo = mappedPointTo.getCoordinates();
      const coordTo = geomTo.getCoordinates();
      if (coordinateEquals(mappedCoordTo, coordTo)) {
        const route = await this.requestRoute(geomFrom, geomTo);
        segment.setGeometry(route);
      }
    }
  }

  /**
   * @param {ol.Feature} controlPoint
   * @param {ol.geom.Point} mappedPoint
   * @param {boolean} force - force snapping (skip this.snap)
   * @private
   */
  snapPoint_(controlPoint, mappedPoint, force = false) {
    const pointGeom = /** @type {ol.geom.Point} */ (controlPoint.getGeometry());
    if (!this.isValidSnap(pointGeom, mappedPoint)) {
      this.dispatchSegmentChangeEvent();
      return;
    }

    // use the mapped point geometry
    controlPoint.setGeometry(mappedPoint);
    this.clearSketchPoint_();
    this.updateControlPoint_(controlPoint);

    // try to snap the two segments
    const promises = ['segmentIn', 'segmentOut'].map(function(name) {
      const draggedPoint = controlPoint;
      const segment = /** @type {ol.Feature} */ (draggedPoint.get(name));
      if (segment !== null && (this.snap || force)) {
        const otherPoint = /** @type {ol.Feature} */ (
          (segment.get('from') === draggedPoint) ? segment.get('to') : segment.get('from'));

        return this.requestSnapPoint(otherPoint).then(
          this.tryRequestRoute_.bind(this, otherPoint, segment)
        );
      } else {
        return jQuery.when(null);
      }
    }.bind(this));

    // Dispatch event once when all promises have resolved
    return jQuery.when.apply(jQuery, promises).then(function() {
      if (!force) {
        this.dispatchSegmentChangeEvent();
      }
    }.bind(this));
  }

  /**
   * @param {ol.Feature} otherPoint
   * @param {ol.Feature} segment
   * @param {ol.geom.Point} mappedPoint
   * @return {jQuery.Promise} promise
   * @private
   */
  tryRequestRoute_(otherPoint, segment, mappedPoint) {
    const pointGeom = /** @type {ol.geom.Point} */ (otherPoint.getGeometry());
    if (!this.isInTolerance(pointGeom, mappedPoint, this.onNetworkTolerance)) {
      return jQuery.when(null);
    }
    // both points are on the network, request a route
    const featureFrom = /** @type {ol.Feature} */ (segment.get('from'));
    const pointFrom = /** @type {ol.geom.Point} */ (featureFrom.getGeometry());

    const featureTo = /** @type {ol.Feature} */ (segment.get('to'));
    const pointTo = /** @type {ol.geom.Point} */ (featureTo.getGeometry());

    return this.requestRoute(pointFrom, pointTo).then((route) => {
      segment.setGeometry(route);
      console.assert(this.isValidTrack());
    });
  }

  /**
   * @param {ol.Feature} controlPoint The control point to remove.
   * @return {ol.Feature|undefined} The new segment if one was created.
   * @private
   */
  removeControlPoint_(controlPoint) {
    let segment;
    if (controlPoint.get('segmentIn') === null) {
      // this is the first point of the track, make the next point the first one
      segment = /** @type {ol.Feature} */ (controlPoint.get('segmentOut'));
      const nextPoint = /** @type {ol.Feature} */ (segment.get('to'));
      if (nextPoint.get('segmentOut') !== null) {
        // We require at least 2 points otherwise the user is in a dead end
        nextPoint.set('segmentIn', null);
        this.removeSegment_(segment);
        this.removePoint_(controlPoint);
        this.dispatchSegmentChangeEvent();
      }
    } else if (controlPoint.get('segmentOut') === null) {
      // We require the track to be at least 2 points
      segment = /** @type {ol.Feature} */ (controlPoint.get('segmentIn'));
      const previousPoint = /** @type {ol.Feature} */ (segment.get('from'));

      if (previousPoint.get('segmentIn') !== null) {
        // We require the track to be at least 2 points
        previousPoint.set('segmentOut', null);
        this.removeSegment_(segment);
        this.removePoint_(controlPoint);
        this.dispatchSegmentChangeEvent();
      }
    } else {
      // the point has a predecessor and a successor, we have to create a new
      // segment between these two and remove the old segments.
      const segmentIn = /** @type {ol.Feature} */ (controlPoint.get('segmentIn'));
      const segmentOut = /** @type {ol.Feature} */ (controlPoint.get('segmentOut'));
      const pointFrom = /** @type {ol.Feature} */ (segmentIn.get('from'));
      const pointTo = /** @type {ol.Feature} */ (segmentOut.get('to'));

      segment = this.createSegment_(pointFrom, pointTo);

      // remove two old segments and insert new segment
      const oldIndex = this.segments.indexOf(segmentIn);
      this.segments.splice(oldIndex, 2, segment);

      this.segmentSource.addFeature(segment);
      this.snapFeatures_.push(segment);

      this.removeSegment_(segmentIn, false);
      this.removeSegment_(segmentOut, false);
      this.removePoint_(controlPoint);
      return segment;
    }
  }

  /**
   * @param {ol.Feature} segment The segment to remove.
   * @param {boolean=} opt_all Remove also from segment list.
   * @private
   */
  removeSegment_(segment, opt_all) {
    if (opt_all === undefined || opt_all) {
      olArrayRemove(this.segments, segment);
    }
    this.segmentSource.removeFeature(segment);
    this.snapFeatures_.remove(segment);
  }

  /**
   * @param {ol.Feature} controlPoint The control point to remove.
   * @private
   */
  removePoint_(controlPoint) {
    olArrayRemove(this.controlPoints, controlPoint);
    this.pointSource.removeFeature(controlPoint);
    this.snapFeatures_.remove(controlPoint);
  }

  /**
   * @param {ol.MapBrowserEvent} event Event.
   * @param {boolean} smallPoint Show a small point?.
   * @private
   */
  createOrUpdateSketchPoint_(event, smallPoint) {
    const coordinates = event.coordinate.slice(0, 2);
    if (!this.sketchPoint_) {
      this.sketchPoint_ = new olFeature(new olGeomPoint(coordinates));
      console.assert(this.sketchSource_.getFeatures().length === 0);
      this.sketchSource_.addFeature(this.sketchPoint_);
    } else {
      const sketchPointGeom = this.sketchPoint_.getGeometry();
      console.assert(sketchPointGeom instanceof olGeomPoint,
        'sketchPointGeom should be an ol.geom.Point');
      sketchPointGeom.setCoordinates(coordinates);
    }
    this.sketchPoint_.set('small', smallPoint);
  }

  isValidTrackFeature(feature) {
    const lineFeature = feature.getGeometry().getType() === 'LineString';
    const validLine = lineFeature && feature.get('from') && feature.get('to');
    const pointFeature = feature.getGeometry().getType() === 'Point';
    const validPoint = pointFeature && (feature.get('segmentIn') || feature.get('segmentOut'));
    return validLine || validPoint;
  }

  reverseTrack() {
    this.controlPoints = this.controlPoints.reverse();
    this.segments = this.segments.reverse();
    for (let i = 0; i < this.controlPoints.length - 1; i++) {
      const segment = this.segments[i];
      const fromPoint = this.controlPoints[i];
      const toPoint = this.controlPoints[i + 1];
      const geometry = segment.get('geometry');
      geometry.setCoordinates(geometry.getCoordinates().reverse());
      segment.set('geometry', geometry);
      segment.set('from', fromPoint);
      segment.set('to', toPoint);
      fromPoint.set('segmentOut', segment);
      toPoint.set('segmentIn', segment);
      if (i === 0) {
        fromPoint.set('segmentIn', null);
      }
      if (i === (this.controlPoints.length - 1) - 1) {
        toPoint.set('segmentOut', null);
      }
    }
    this.pointSource.clear();
    this.pointSource.addFeatures(this.controlPoints);
    this.segmentSource.clear();
    this.segmentSource.addFeatures(this.segments);
  }

  snapTrack() {
    this.snapInteraction_.setActive(false);
    return Promise.all(this.controlPoints.map(point => {
      return this.requestSnapPoint(point).then((mappedPoint) => {
        return this.snapPoint_(point, mappedPoint, true);
      });
    })).then(() => {
      this.dispatchSegmentChangeEvent();
      this.snapInteraction_.setActive(true);
    });
  }

  updateSketchLineColor(color_rgb, opacity) {
    this.sketchLineColor = {
      color_rgb: color_rgb,
      opacity: opacity
    };
    this.pointLayer_.changed();
    this.segmentLayer_.changed();
  }
}


export default ModifyTrack;
